import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "../context/SiteContext";
import { scrollToHash } from "../lib/api";

const NAV = [
    { label: { en: "WHO WE ARE", pt: "QUEM SOMOS" }, hash: "#who-we-are", testId: "nav-who-link" },
    { label: { en: "WHAT WE DO", pt: "O QUE FAZEMOS" }, hash: "#what-we-do", testId: "nav-what-link" },
    { label: { en: "TALENTS", pt: "TALENTOS" }, hash: "#talent", testId: "nav-talent-link" },
    { label: { en: "PROJECTS", pt: "PROJETOS" }, hash: "#projects", testId: "nav-projects-link" },
    { label: { en: "PEOPLE", pt: "PESSOAS" }, hash: "#people", testId: "nav-people-link" },
    { label: { en: "CONTACT", pt: "CONTACTO" }, hash: "#contact", testId: "nav-contact-link" },
];

export default function Header() {
    const { lang, setLang, tx } = useSite();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (hash) => (e) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => scrollToHash(hash), open ? 350 : 0);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled ? "bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/10" : "bg-black/25 backdrop-blur-md"
                }`}
            >
                <div className="flex items-center justify-between px-6 py-4 md:px-12">
                    <a href="#hero" onClick={go("#hero")} data-testid="header-logo" className="font-display leading-[0.8] text-white select-none">
                        <span className="block text-lg tracking-wide">BEYOND</span>
                        <span className="block text-lg tracking-wide">THE FIELD<span className="text-[#D60024]">+</span></span>
                    </a>

                    <nav className="hidden lg:flex items-center gap-8">
                        {NAV.map((item) => (
                            <a
                                key={item.hash}
                                href={item.hash}
                                onClick={go(item.hash)}
                                data-testid={item.testId}
                                className="u-line text-[11px] font-semibold tracking-[0.22em] text-white/85 hover:text-white transition-colors"
                            >
                                {tx(item.label)}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em]">
                            <button data-testid="lang-toggle-en" onClick={() => setLang("en")} className={lang === "en" ? "text-[#D60024]" : "text-white/50 hover:text-white transition-colors"}>EN</button>
                            <span className="text-white/30">/</span>
                            <button data-testid="lang-toggle-pt" onClick={() => setLang("pt")} className={lang === "pt" ? "text-[#D60024]" : "text-white/50 hover:text-white transition-colors"}>PT</button>
                        </div>
                        <button
                            data-testid="menu-toggle-btn"
                            aria-label="Menu"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden text-white font-display text-3xl leading-none w-10 h-10 flex items-center justify-center border border-white/25 hover:border-[#D60024] hover:text-[#D60024] transition-colors"
                        >
                            <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="block">+</motion.span>
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 bg-[#0A0A0A] flex flex-col justify-center px-8"
                        data-testid="mobile-menu"
                    >
                        {NAV.map((item, i) => (
                            <div key={item.hash} className="overflow-hidden border-b border-white/10">
                                <motion.a
                                    href={item.hash}
                                    onClick={go(item.hash)}
                                    data-testid={`mobile-${item.testId}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    exit={{ y: "110%" }}
                                    transition={{ duration: 0.5, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                                    className="block py-4 font-display text-5xl text-white hover:text-[#D60024] transition-colors"
                                >
                                    {tx(item.label)}
                                </motion.a>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
